import { SITE } from "@/config/site";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Reveal } from "@/components/Reveal";
import { StarPattern } from "@/components/Ornament";
import "@/styles/theme-v2.css";

// ============================================================================
// CHAPTER 04 — EVENT DETAILS. Same rows, same values, same chips, same
// note — nothing in the data changes. Visually this used to be a single
// text column with a lot of unused width next to each short value on
// desktop; it now sits alongside a companion photo panel using the same
// real venue photo already approved and live in Chapter 06 (not a new
// image, not a different crop of anyone's likeness) so the details read
// against the actual room they describe.
// ============================================================================

export function EventDetails() {
  const s = SITE.schedule;
  const rows = [
    { label: "Date", value: SITE.date, testId: "details-date" },
    { label: "Art Exhibition", value: s.exhibition, testId: "details-exhibition" },
    { label: "Ticket Scanning & Box Office", value: s.boxOffice, testId: "details-box-office" },
    { label: "Auditorium Entry", value: s.entry, testId: "details-entry" },
    { label: "Show Begins", value: s.show, testId: "details-show" },
    { label: "Duration", value: s.duration, testId: "details-duration" },
    { label: "Venue", value: SITE.venue.name, testId: "details-venue" },
    {
      label: "Location",
      value: `${SITE.venue.area}, ${SITE.venue.city}`,
      testId: "details-location",
    },
  ];

  const chips = [
    { text: `Age limit ${SITE.ageLimit}`, testId: "details-chip-age" },
    { text: SITE.languages.join(" • "), testId: "details-chip-languages" },
    { text: SITE.parking, testId: "details-chip-parking" },
    { text: SITE.venue.capacity, testId: "details-chip-capacity" },
  ];

  const v = SITE.venue;

  return (
    <section id="details" data-testid="details-section" className="relative overflow-hidden px-5 py-20 sm:px-8 sm:py-28 lg:py-36">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-24 bottom-0 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/[0.06] blur-[120px]" />
      </div>
      <div className="relative mx-auto max-w-6xl">
        <ChapterHeading number="05" title="Event Details" urdu="تفصیلات" testId="details-heading" />

        {/* Compact mobile/tablet venue image — same approved photo as Chapter
            07, but a short horizontal band rather than that chapter's tall
            portrait frame, so it reads as a visual break here rather than a
            duplicate of the full Venue treatment. Desktop keeps the existing
            sticky portrait panel below (lg:col-span-5) instead. */}
        {v.hasPhoto && v.photo && (
          <div className="mt-10 lg:hidden">
            <Reveal delay={0.1}>
              <div className="relative h-40 w-full overflow-hidden rounded-3xl border border-[#D4AF37]/25 bg-[#0E0C0A] sm:h-48">
                <img
                  src={v.photo}
                  alt={`${v.name} — venue`}
                  data-testid="details-venue-photo-mobile"
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ objectPosition: "50% 22%" }}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div
                  className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(8,7,5,0.1)_0%,transparent_45%,rgba(8,7,5,0.55)_100%)]"
                  aria-hidden="true"
                />
                <div className="pointer-events-none absolute inset-[6px] border border-[#F3E5AB]/10" aria-hidden="true" />
                <p className="absolute inset-x-4 bottom-3 font-display text-sm leading-snug text-[#F7F4EF]">
                  {v.name}
                </p>
              </div>
            </Reveal>
          </div>
        )}

        <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <div className="divide-y divide-[#D4AF37]/15 border-y border-[#D4AF37]/15">
              {rows.map((r, i) => (
                <Reveal key={r.label} delay={i * 0.05}>
                  <div className="grid grid-cols-1 gap-2 py-6 sm:grid-cols-[220px_1fr] sm:items-baseline sm:py-8">
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] sm:text-sm">
                      {r.label}
                    </p>
                    <p
                      data-testid={r.testId}
                      className="font-display text-2xl leading-tight text-[#F7F4EF] sm:text-3xl"
                    >
                      {r.value}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-10 flex flex-wrap gap-3">
                {chips.map((c) => (
                  <span
                    key={c.testId}
                    data-testid={c.testId}
                    className="inline-flex items-center rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-5 py-2.5 text-sm font-semibold text-[#F3E5AB] sm:text-base"
                  >
                    {c.text}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.26}>
              <p className="mt-8 text-base leading-relaxed text-[#918A7E] sm:text-lg">
                {SITE.seatingNote} Arrive early for the best seats in your category.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.16} className="hidden lg:col-span-5 lg:block">
            <div className="sticky top-28 max-w-[320px]">
              <div className="arch-frame relative aspect-[3/4.2] w-full overflow-hidden border border-[#D4AF37]/30 bg-[#0E0C0A]">
                {v.hasPhoto && v.photo ? (
                  <img
                    src={v.photo}
                    alt={`${v.name} — venue`}
                    data-testid="details-venue-photo"
                    className="cah-kenburns absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition: "50% 22%" }}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <>
                    <StarPattern id="details-photo-pattern" opacity={0.25} />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_22%,rgba(212,175,55,0.18),transparent_65%)]" />
                  </>
                )}
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 bg-[linear-gradient(180deg,transparent_55%,rgba(8,7,5,0.7)_100%)]"
                  aria-hidden="true"
                />
                <div className="pointer-events-none absolute inset-[8px] border border-[#F3E5AB]/15" aria-hidden="true" />
                <p className="absolute inset-x-6 bottom-6 font-display text-lg leading-snug text-[#F7F4EF]">
                  {v.name}
                </p>
              </div>
              <div
                className="arch-frame absolute -right-4 top-4 -z-10 h-full w-full border border-[#D4AF37]/15"
                aria-hidden="true"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
